import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";
import { styled } from "@mui/material/styles";

/**
 * Поджатие видео под инстрим-брендинг и баннер-уголок.
 *
 * Раньше контейнер анимировал bottom/left/right/top (transition: all): секунду
 * видео перестраивало раскладку на каждом кадре, и в это время мелькала
 * чернота, а сверху появлялась чёрная полоса. Теперь размер контейнера не
 * меняется, видео уменьшается через transform: scale (только композитинг).
 * - in-stream: масштаб (H - высота баннера) / H, прижато к верхнему краю,
 *   снизу освобождается место ровно под баннер;
 * - уголок: 80% по ширине и высоте, прижато к нижнему краю по центру (как
 *   прежние left/right 10% и top 20%).
 */
export const CORNER_SCALE = 0.8;
const ANIMATION_MS = 1000;
const ANIMATION = `transform ${ANIMATION_MS}ms ease`;

const classes = {
  transparentVideo: "PlayerInView-transparentVideo",
};

const Root = styled("div")({
  display: "block",
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  overflow: "hidden",
  zIndex: 2,
  transition: ANIMATION,
  willChange: "transform",

  [`&.${classes.transparentVideo} video`]: {
    background: "transparent",
  },
});

export function getPlayerTransform({
  containerHeight,
  inStreamBannerHeight,
  cornerBannerHeight,
  previousOrigin,
}) {
  if (cornerBannerHeight > 0) {
    return { transform: `scale(${CORNER_SCALE})`, origin: "center bottom" };
  }

  if (inStreamBannerHeight > 0 && containerHeight > 0) {
    const scale = Math.max(
      0,
      (containerHeight - inStreamBannerHeight) / containerHeight
    );

    return { transform: `scale(${scale})`, origin: "center top" };
  }

  // Возврат к полному размеру анимируется от той же точки, что и поджатие:
  // смена transform-origin в начале анимации давала скачок видео вверх
  return { transform: "none", origin: previousOrigin || "center top" };
}

export default function PlayerInView({
  cornerBannerHeight,
  inStreamBannerHeight,
  onChange,
  children,
}) {
  const nodeRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const originRef = useRef(undefined);
  const [transparent, setTransparent] = useState(false);

  const { ref: inViewRef } = useInView({ threshold: 0.5, onChange });

  const setRefs = useCallback(
    (node) => {
      nodeRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      setContainerHeight(node.offsetHeight);
    });

    setContainerHeight(node.offsetHeight);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const { transform, origin } = getPlayerTransform({
    containerHeight,
    inStreamBannerHeight,
    cornerBannerHeight,
    previousOrigin: originRef.current,
  });
  originRef.current = origin;

  // Рамка: поля вписанного видео (плеер шире 16:9) прозрачные, в них видна
  // рамка, а не чёрные полосы. Прозрачность держим до конца обратной
  // анимации, иначе полосы почернели бы во время возврата.
  const cornerActive = cornerBannerHeight > 0;

  useEffect(() => {
    if (cornerActive) {
      setTransparent(true);
      return undefined;
    }

    const timer = setTimeout(() => setTransparent(false), ANIMATION_MS);

    return () => clearTimeout(timer);
  }, [cornerActive]);

  return (
    <Root
      ref={setRefs}
      className={transparent ? classes.transparentVideo : undefined}
      style={{ transform, transformOrigin: origin }}
    >
      {children}
    </Root>
  );
}

PlayerInView.propTypes = {
  cornerBannerHeight: PropTypes.number,
  inStreamBannerHeight: PropTypes.number,
  onChange: PropTypes.func,
  children: PropTypes.node,
};
